import React, { useEffect } from 'react';
import { faEnvelope } from '@fortawesome/free-solid-svg-icons/faEnvelope';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';

import Link from '@buyside/components/presentation/Link/link';
import NavigationMessage from '@buyside/components/presentation/MainSiteNavigation/TopNavigation/NavigationMessages/navigationMessage';
import NavigationMessageLoading from '@buyside/components/presentation/MainSiteNavigation/TopNavigation/NavigationMessages/navigationMessageLoading';

import { accessIdeasUrl, messagesUrl } from '@buyside/util/urlHelper';
import useDropdownButton from '@buyside/util/hooks/useDropdownButton';

import {
  useMainSiteNavigationMessagesQuery,
  useUpdateCurrentUserLastViewedMessagesAtMutation
} from '@interfaces/graphql-typings';

import styles from './navigationMessagesStyles.scss';

const NavigationMessages: React.FC<{ showUpsell: boolean; unseenMessageCount: number }> = ({
  showUpsell,
  unseenMessageCount
}) => {
  const { dropdownRef, dropdownVisible, toggleDropdown } = useDropdownButton();

  const [resetLastViewedMessages] = useUpdateCurrentUserLastViewedMessagesAtMutation();

  const { data, loading: messagesLoading, error } = useMainSiteNavigationMessagesQuery({
    skip: showUpsell
  });

  if (error) throw error;

  const { incomingMessageThreads, unreadMessageCount } = data?.currentUser || {};

  useEffect(() => {
    ((): void => {
      if (dropdownVisible && Boolean(unreadMessageCount)) {
        resetLastViewedMessages();
      }
    })();
  }, [resetLastViewedMessages, unreadMessageCount, dropdownVisible]);

  return (
    <div ref={dropdownRef} className={styles.dropdown}>
      <button
        data-heap={showUpsell ? 'request-access-button' : null}
        data-testid="messages-dropdown-button"
        className={classNames(styles.dropdownButton, {
          [styles.active]: dropdownVisible
        })}
        onClick={async (): Promise<void> => {
          if (showUpsell) window.location.href = accessIdeasUrl();
          return toggleDropdown();
        }}
      >
        <div className="fa-layers">
          <FontAwesomeIcon icon={faEnvelope} />
          {Boolean(unseenMessageCount) && (
            <span className="fa-layers-counter">{unseenMessageCount}</span>
          )}
        </div>
      </button>
      <div
        className={classNames(styles.dropdownAreaWrapper, {
          [styles.visible]: dropdownVisible
        })}
      >
        <div className={styles.dropdownArea}>
          <div className={styles.dropdownHeader}>Messages</div>
          <div className={styles.messagesList}>
            {!messagesLoading && !incomingMessageThreads?.length && (
              <div className={styles.messagesListEmpty}>No Messages</div>
            )}
            {incomingMessageThreads?.map(message =>
              message ? <NavigationMessage key={message.id} message={message} /> : null
            )}
            {messagesLoading && [1, 2, 3].map(key => <NavigationMessageLoading key={key} />)}
          </div>
          <div className={styles.dropdownActionArea}>
            <Link analyticsTag="view-all-messages" to={messagesUrl()}>
              <FontAwesomeIcon icon={faEnvelope} />
              View All Messages{' '}
              {Boolean(unreadMessageCount) && `(${unreadMessageCount} more unread)`}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NavigationMessages;
